/**
 * QuestLabOrderModal.tsx
 *
 * Thin Quest adapter over the shared LabOrderModalCore. All form UI/logic lives
 * in the core now; this file only wires the Quest-specific services, sections,
 * preset HL7 parsing, submit-response parsing, and the V4 HMAC auth-debug
 * extractor. Behavior is intentionally identical to the pre-refactor modal so
 * existing Quest ordering is unaffected.
 */
import axios from 'axios';
import {
  getQuestFacilities,
  getQuestProviders,
  searchQuestTests,
  searchInsuranceProviders,
  searchDiagnoses,
  submitQuestOrder,
  saveQuestPreset,
  getQuestPresets,
  getQuestPresetContent,
  parseQuestOrderResponse,
} from '@/services/labs.service';
import { parseQuestHl7Order } from './questHl7';
import { LabOrderModalCore, type LabOrderConfig, type LabOrderSubmitResult, type ParsedLabPreset } from './LabOrderModalCore';

interface QuestLabOrderModalProps {
  patientId: number;
  open: boolean;
  onClose: () => void;
  onOrdered: () => void;
}

/**
 * Extract a one-line summary of the V4 forward-auth debug envelope from an
 * axios error (attached by ForwardInternalAuth::fail on V4 and surfaced through
 * LabOrdersController::forwardResultResponse under error.details.v4Debug).
 */
function extractV4AuthDebugSummary(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null;
  const data = err.response?.data as { error?: { details?: unknown } } | undefined;
  const details = data?.error?.details;
  if (!details || typeof details !== 'object') return null;
  const v4Debug = (details as { v4Debug?: unknown }).v4Debug;
  if (!v4Debug || typeof v4Debug !== 'object') return null;
  const debug = v4Debug as Record<string, Record<string, unknown>>;
  const parts: string[] = [];
  const pick = (section: string, key: string, label?: string) => {
    const value = debug[section]?.[key];
    if (value === undefined || value === null) return;
    parts.push(`${label ?? key}=${String(value)}`);
  };
  pick('result', 'reason');
  pick('env', 'expectedKeyIdPresent');
  pick('env', 'hmacSecretPresent');
  pick('headers', 'keyIdLength');
  pick('auth', 'keyIdMatches');
  pick('headers', 'tsIsDigits');
  pick('headers', 'nonceLength');
  pick('headers', 'nonceFormatValid');
  pick('headers', 'signatureLength');
  pick('headers', 'signatureFormatValid');
  pick('headers', 'accountIdGtZero');
  pick('auth', 'timestampSkewSeconds');
  pick('auth', 'signatureMatches');
  pick('request', 'requestPath');
  return parts.length > 0 ? parts.join(', ') : null;
}

const questConfig: LabOrderConfig = {
  vendor: 'quest',
  title: 'Quest Lab Order',
  features: { presets: true, facilityProvider: true, billing: true, diagnoses: true },
  searchTests: searchQuestTests,
  getFacilities: getQuestFacilities,
  getProviders: getQuestProviders,
  searchInsurance: searchInsuranceProviders,
  searchDiagnoses,
  getPresets: getQuestPresets,
  getPresetContent: getQuestPresetContent,
  parsePreset: (content, kind, defaultDate) =>
    parseQuestHl7Order(content, kind, defaultDate) as unknown as ParsedLabPreset,
  savePreset: saveQuestPreset,
  extractErrorDebug: extractV4AuthDebugSummary,
  submit: async (payload): Promise<LabOrderSubmitResult> => {
    const result = await submitQuestOrder(payload);
    const lines = parseQuestOrderResponse(result.response);
    const errorLine = lines.find((l) => l.isError);
    if (errorLine) {
      return { ok: false, lines: [], errorMessage: errorLine.message || 'The order could not be submitted.' };
    }
    return { ok: true, lines: lines.map((l) => `Order #${l.orderId}: ${l.message}`) };
  },
};

export function QuestLabOrderModal({ patientId, open, onClose, onOrdered }: QuestLabOrderModalProps) {
  return (
    <LabOrderModalCore
      config={questConfig}
      patientId={patientId}
      open={open}
      onClose={onClose}
      onOrdered={onOrdered}
    />
  );
}
